import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox, Selector, type SelectorOptionPropsInterface, TextArea, Toast } from 'sonata-react-components';
import { DoohAgeVariables } from 'sonata-static-storage/lib/enum/doohAudienceVariable.enum';

import { FormExpandableSection } from '@/components/common/FormExpandableSection/FormExpandableSection';
import { EMPTY_DOOH_AUDIENCE } from '@/helpers/doohAudience/doohAudience.helper';
import { TargetingCard } from '../../common/TargetingCard';
import { GenderCards } from '../GenderCards';
import {
  buildDoohVariables,
  type DoohGender,
  getDoohAgeVariables,
  getDoohDemographyTags,
  getDoohGender,
  getSocialMediaVariables
} from './doohDemography.helpers';
import { useSocialMediaOptions } from './useSocialMediaOptions';

import './doohDemography.scss';

import type { DoohAudience } from '@/services/doohAudience.service';

export const DOOH_DEMOGRAPHY_TEXT_MAX_LENGTH = 500;

const AGE_ALL_CARD_ICON = 'fa-solid fa-id-card';
const AGE_RANGE_CARD_ICON = 'fa-solid fa-user';

type Mode = 'ai' | 'manual';

type Props = {
  country?: string;
  doohAudience: DoohAudience;
  /** A finished entity: its audience can no longer change */
  isReadOnly?: boolean;
  onChange: (doohAudience: DoohAudience) => void;
};

/**
 * Audience target of a DOOH entity, the one its coverage study runs for. Once enabled, in one of two modes: manual (the
 * selected gender, age ranges and social media variables; none selected is the total population) or AI (a free text
 * the AI turns into the variables once the entity is saved). The variables of the AI are never shown: switching mode
 * starts the other one empty.
 */
function DoohDemography({ country, doohAudience, isReadOnly = false, onChange }: Props) {
  const { formatMessage } = useIntl();
  const { byAI, enableGenerateReport, text, variables } = doohAudience;
  const [mode, setMode] = useState<Mode>(text || byAI ? 'ai' : 'manual');
  const { isLoading: isLoadingSocialMedia, options: socialMediaOptions } = useSocialMediaOptions(country);
  const gender = getDoohGender(variables);
  const ages = getDoohAgeVariables(variables);
  const socialMedia = getSocialMediaVariables(variables);

  const handleEnabledChange = (isEnabled: boolean) => onChange({ ...doohAudience, enableGenerateReport: isEnabled });

  const handleModeChange = (isAIMode: boolean) => {
    setMode(isAIMode ? 'ai' : 'manual');
    onChange({ byAI: false, enableGenerateReport: true, ...(isAIMode && { text: '' }), variables: [] });
  };

  const handleReset = () => {
    setMode('manual');
    onChange(EMPTY_DOOH_AUDIENCE);
  };

  const changeSelection = (newGender: DoohGender, newAges: string[], newSocialMedia: string[]) =>
    onChange({
      byAI: false,
      enableGenerateReport: true,
      variables: buildDoohVariables(newGender, newAges, newSocialMedia)
    });

  const handleAgeClick = (variable: string) =>
    changeSelection(
      gender,
      ages.includes(variable) ? ages.filter(age => age !== variable) : [...ages, variable],
      socialMedia
    );

  const handleSocialMediaChange = (values: SelectorOptionPropsInterface[]) =>
    changeSelection(
      gender,
      ages,
      values.map(({ id }) => String(id))
    );

  const handleTextChange = (value: string) =>
    onChange({ byAI: false, enableGenerateReport: true, text: value, variables: [] });

  const socialMediaSelected = socialMediaOptions.filter(({ id }) => socialMedia.includes(id));
  const ageSubtitle = formatMessage({ defaultMessage: 'Age', id: 'targeting.doohDemography.age.subtitle' });
  const tags = getDoohDemographyTags(doohAudience, {
    ages: count =>
      formatMessage(
        {
          defaultMessage: '{count, plural, one {# age range} other {# age ranges}}',
          id: 'targeting.doohDemography.tags.ages'
        },
        { count }
      ),
    ai: formatMessage({ defaultMessage: 'AI target', id: 'targeting.doohDemography.tags.ai' }),
    female: formatMessage({ defaultMessage: 'Female', id: 'targeting.doohDemography.tags.female' }),
    male: formatMessage({ defaultMessage: 'Male', id: 'targeting.doohDemography.tags.male' }),
    socialMedia: count =>
      formatMessage(
        {
          defaultMessage: '{count, plural, one {# interest} other {# interests}}',
          id: 'targeting.doohDemography.tags.socialMedia'
        },
        { count }
      ),
    totalPopulation: formatMessage({
      defaultMessage: 'Total population',
      id: 'targeting.doohDemography.tags.totalPopulation'
    })
  });

  return (
    <div className="doohDemography" id="demographyBox">
      <FormExpandableSection
        defaultOpen
        onReset={() => !isReadOnly && handleReset()}
        preffixId="demography"
        tags={tags}
        title={formatMessage({ defaultMessage: 'Audience Targeting', id: 'targeting.doohDemography.title' })}
        tooltipText={formatMessage({
          defaultMessage: 'The audience the coverage study of the DOOH report is run for.',
          id: 'targeting.doohDemography.tooltip'
        })}
      >
        <fieldset className="doohDemography__content" disabled={isReadOnly}>
          {isReadOnly && (
            <Toast
              autoClose={false}
              className="doohDemography__toast"
              closeButton={false}
              icon="fa-regular fa-triangle-exclamation"
              text={formatMessage({
                defaultMessage: 'This campaign has finished: its audience can no longer change.',
                id: 'targeting.doohDemography.readOnly'
              })}
              type="warning"
            />
          )}
          <div className="doohDemography__header">
            <Checkbox
              active={enableGenerateReport}
              colorSchema="secondary"
              disabled={isReadOnly}
              id="doohDemographyEnabled"
              labelText={formatMessage({
                defaultMessage: 'Enable audience targeting',
                id: 'targeting.doohDemography.enabled'
              })}
              onClickAction={(_id: string, checked: boolean) => handleEnabledChange(checked)}
              type="switch"
            />
            {enableGenerateReport && (
              <Checkbox
                active={mode === 'ai'}
                colorSchema="secondary"
                disabled={isReadOnly}
                id="doohDemographyAiMode"
                labelText={formatMessage({ defaultMessage: 'Generate with AI', id: 'targeting.doohDemography.aiMode' })}
                onClickAction={(_id: string, checked: boolean) => handleModeChange(checked)}
                type="switch"
              />
            )}
          </div>
          {!enableGenerateReport && (
            <span className="doohDemography__hint">
              {formatMessage({
                defaultMessage: 'Enable it to generate the DOOH report of this campaign for its audience.',
                id: 'targeting.doohDemography.disabled'
              })}
            </span>
          )}
          {enableGenerateReport && mode === 'manual' && (
            <div className="doohDemography__selection">
              <span className="doohDemography__title">
                {formatMessage({ defaultMessage: 'Gender', id: 'targeting.doohDemography.gender.title' })}
              </span>
              <GenderCards
                className="doohDemography__cardList"
                onSelect={id => changeSelection(id, ages, socialMedia)}
                selected={gender}
              />
              <span className="doohDemography__title">
                {formatMessage({ defaultMessage: 'Age', id: 'targeting.doohDemography.age.title' })}
              </span>
              <div className="doohDemography__cardList">
                <TargetingCard
                  icon={AGE_ALL_CARD_ICON}
                  isSelected={ages.length === 0}
                  label={formatMessage({ defaultMessage: 'All (18+)', id: 'targeting.doohDemography.age.all' })}
                  onClick={() => changeSelection(gender, [], socialMedia)}
                  subtitle={ageSubtitle}
                />
                {Object.entries(DoohAgeVariables).map(([range, variable]) => (
                  <TargetingCard
                    icon={AGE_RANGE_CARD_ICON}
                    isSelected={ages.includes(variable)}
                    key={variable}
                    label={range}
                    onClick={() => handleAgeClick(variable)}
                    subtitle={ageSubtitle}
                  />
                ))}
              </div>
              <Selector
                isClearable
                isDisabled={isReadOnly || !country || isLoadingSocialMedia}
                label={formatMessage({
                  defaultMessage: 'Social media',
                  id: 'targeting.doohDemography.socialMedia.title'
                })}
                onChange={handleSocialMediaChange}
                options={socialMediaOptions}
                placeholder={formatMessage({
                  defaultMessage: 'Select interests',
                  id: 'targeting.doohDemography.socialMedia.placeholder'
                })}
                type="multiple"
                value={socialMediaSelected}
              />
            </div>
          )}
          {enableGenerateReport && mode === 'ai' && (
            <div className="doohDemography__text">
              <Toast
                autoClose={false}
                className="doohDemography__toast"
                closeButton={false}
                icon="fa-regular fa-circle-info"
                text={formatMessage({
                  defaultMessage:
                    'AI will choose the best audience setup based on what you write. It will be calculated once you save.',
                  id: 'targeting.doohDemography.disclaimer'
                })}
                type="info"
              />
              <span className="doohDemography__title">
                {formatMessage({ defaultMessage: 'Describe your target', id: 'targeting.doohDemography.text.title' })}
              </span>
              <TextArea
                className="doohDemography__textArea"
                disabled={isReadOnly}
                height={190}
                id="doohDemographyText"
                maxLength={DOOH_DEMOGRAPHY_TEXT_MAX_LENGTH}
                onChange={handleTextChange}
                placeholder={formatMessage({
                  defaultMessage: 'e.g. Young urban women interested in fashion and beauty',
                  id: 'targeting.doohDemography.text.placeholder'
                })}
                showLengthCounter
                value={text ?? ''}
              />
              {!text?.trim() && (
                <span className="doohDemography__hint">
                  {formatMessage({
                    defaultMessage: 'Without a text, the audience is the total population.',
                    id: 'targeting.doohDemography.text.empty'
                  })}
                </span>
              )}
            </div>
          )}
        </fieldset>
      </FormExpandableSection>
    </div>
  );
}

export default DoohDemography;
